/**
 * Inventory cosmetic drag: custom ghost (not browser drag image) with gel stretch.
 * Supports equip-to-zone and unequip-when-dropped-outside-keep-zones.
 */

export type InventoryDragResult = "equipped" | "unequipped" | "cancelled";

type EquipMode = {
  kind: "equip";
  category: string;
  dropZones: HTMLElement[];
  onDropEquip: () => Promise<boolean>;
};

type UnequipMode = {
  kind: "unequip";
  /** Dropping inside any of these cancels unequip (item stays equipped). */
  keepZones: HTMLElement[];
  onUnequip: () => Promise<boolean>;
};

export type InventoryDragOpts = {
  sourcePreview: HTMLElement;
  /** Canvas (or element) whose pixels are cloned into the floating ghost. */
  sourceVisual: HTMLCanvasElement | HTMLElement;
  mode: EquipMode | UnequipMode;
  /** CSS pixel size for the floating ghost (needed for detached canvases). */
  ghostDisplaySize?: { width: number; height: number };
};

const SETTLE_MS = 420;
const RETURN_MS = 380;
/** Touch / coarse pointer must hold this long before a drag starts (lets scroll win). */
export const INVENTORY_TOUCH_HOLD_MS = 200;
const TOUCH_MOVE_CANCEL_PX = 12;
/** Match inventory card preview display size. */
export const INVENTORY_GHOST_SIZE = 220;

let scrollLockCount = 0;
let savedScrollY = 0;
let savedScrollX = 0;
const frozenOverflow = new Map<HTMLElement, string>();

function blockTouchScroll(e: TouchEvent) {
  if (scrollLockCount <= 0) return;
  e.preventDefault();
}

function blockWheelScroll(e: Event) {
  if (scrollLockCount <= 0) return;
  e.preventDefault();
}

/** Freeze page + scroll parents so a mobile drag cannot scroll the Inventory. */
export function lockInventoryDragScroll() {
  scrollLockCount += 1;
  if (scrollLockCount > 1) return;

  savedScrollX = window.scrollX;
  savedScrollY = window.scrollY;
  document.documentElement.classList.add("inventoryDragging");
  document.body.classList.add("inventoryDragging");

  const roots: Array<HTMLElement | null> = [
    document.documentElement,
    document.body,
    document.getElementById("inventoryScreen"),
    document.querySelector(".inventoryScreenMain"),
    document.querySelector(".inventoryLivePreviewPane"),
    document.querySelector(".shopScreenMain"),
  ];
  for (const el of roots) {
    if (!el || frozenOverflow.has(el)) continue;
    frozenOverflow.set(el, el.style.overflow);
    el.style.overflow = "hidden";
  }

  document.addEventListener("touchmove", blockTouchScroll, { passive: false, capture: true });
  document.addEventListener("wheel", blockWheelScroll, { passive: false, capture: true });
}

export function unlockInventoryDragScroll() {
  if (scrollLockCount <= 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount > 0) return;

  document.documentElement.classList.remove("inventoryDragging");
  document.body.classList.remove("inventoryDragging");
  for (const [el, prev] of frozenOverflow) {
    el.style.overflow = prev;
  }
  frozenOverflow.clear();
  document.removeEventListener("touchmove", blockTouchScroll, true);
  document.removeEventListener("wheel", blockWheelScroll, true);
  window.scrollTo(savedScrollX, savedScrollY);
}

function zoneAccepts(zone: HTMLElement, category: string): boolean {
  const raw = zone.dataset.dropCats || "";
  if (!raw.trim()) return true;
  return raw
    .split(",")
    .map((s) => s.trim())
    .includes(category);
}

function hitZoneList(clientX: number, clientY: number, zones: HTMLElement[]): HTMLElement | null {
  for (const zone of zones) {
    if (!zone.isConnected) continue;
    const r = zone.getBoundingClientRect();
    if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
      return zone;
    }
  }
  const stack = document.elementsFromPoint(clientX, clientY);
  for (const el of stack) {
    if (!(el instanceof HTMLElement)) continue;
    for (const zone of zones) {
      if (zone.contains(el) || el === zone) return zone;
    }
  }
  return null;
}

function hitDropZone(
  clientX: number,
  clientY: number,
  zones: HTMLElement[],
  category: string,
): HTMLElement | null {
  const accepted = zones.filter((z) => zoneAccepts(z, category));
  const direct = hitZoneList(clientX, clientY, accepted);
  if (direct) return direct;
  const stack = document.elementsFromPoint(clientX, clientY);
  for (const el of stack) {
    if (!(el instanceof HTMLElement)) continue;
    const zone = el.closest<HTMLElement>("[data-inventory-drop]");
    if (zone && zoneAccepts(zone, category)) return zone;
  }
  return null;
}

function copyVisualToCanvas(source: HTMLCanvasElement | HTMLElement, dest: HTMLCanvasElement) {
  const ctx = dest.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, dest.width, dest.height);
  if (source instanceof HTMLCanvasElement && source.width > 0) {
    dest.width = source.width;
    dest.height = source.height;
    ctx.drawImage(source, 0, 0);
    return;
  }
  if (source instanceof HTMLCanvasElement) {
    ctx.drawImage(source, 0, 0, dest.width, dest.height);
  }
}

function resolveGhostDisplaySize(
  sourceVisual: HTMLCanvasElement | HTMLElement,
  explicit?: { width: number; height: number },
): { width: number; height: number } {
  if (explicit && explicit.width >= 8 && explicit.height >= 8) {
    return { width: explicit.width, height: explicit.height };
  }
  const rect = sourceVisual.getBoundingClientRect();
  if (rect.width >= 8 && rect.height >= 8) {
    return { width: rect.width, height: rect.height };
  }
  if (sourceVisual instanceof HTMLCanvasElement && sourceVisual.width > 0 && sourceVisual.height > 0) {
    const isBanner = sourceVisual.width / sourceVisual.height >= 2.5;
    if (isBanner) {
      const width = Math.min(280, Math.max(160, sourceVisual.width));
      return { width, height: width * (sourceVisual.height / sourceVisual.width) };
    }
    const side = Math.min(INVENTORY_GHOST_SIZE, Math.max(160, sourceVisual.width));
    return { width: side, height: side };
  }
  return { width: INVENTORY_GHOST_SIZE, height: INVENTORY_GHOST_SIZE };
}

function animateTransform(
  el: HTMLElement,
  from: { x: number; y: number; sx: number; sy: number },
  to: { x: number; y: number; sx: number; sy: number },
  ms: number,
  overshoot: boolean,
): Promise<void> {
  return new Promise((resolve) => {
    const t0 = performance.now();
    const step = (now: number) => {
      const u = Math.min(1, (now - t0) / ms);
      let e: number;
      if (overshoot) {
        e = 1 - Math.pow(1 - u, 3);
        if (u < 0.72) {
          e = 1.08 * (1 - Math.pow(1 - u / 0.72, 2));
        } else {
          const v = (u - 0.72) / 0.28;
          e = 1.08 - 0.08 * (1 - Math.pow(1 - v, 2));
        }
      } else {
        e = 1 - Math.pow(1 - u, 3);
      }
      const x = from.x + (to.x - from.x) * e;
      const y = from.y + (to.y - from.y) * e;
      const sx = from.sx + (to.sx - from.sx) * Math.min(1, u * 1.2);
      const sy = from.sy + (to.sy - from.sy) * Math.min(1, u * 1.2);
      el.style.transform = `translate(${x}px, ${y}px) scale(${sx}, ${sy})`;
      if (u < 1) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });
}

export function isInventoryTouchPointer(e: PointerEvent): boolean {
  if (e.pointerType === "touch") return true;
  if (e.pointerType === "pen") return true;
  return !window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/**
 * Start drag on pointerdown for mouse; require a short hold on touch so scrolling wins.
 * Returns an unbind function.
 */
export function bindInventoryDragPointer(
  el: HTMLElement,
  onReady: (e: PointerEvent) => void,
  opts?: { isBlocked?: (e: PointerEvent) => boolean },
): () => void {
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let startX = 0;
  let startY = 0;
  let armedPointerId: number | null = null;
  let started = false;
  let holdLocked = false;

  const clearHold = (unlock = true) => {
    if (holdTimer != null) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
    armedPointerId = null;
    started = false;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    if (unlock && holdLocked) {
      holdLocked = false;
      unlockInventoryDragScroll();
    }
  };

  const onMove = (e: PointerEvent) => {
    if (armedPointerId !== e.pointerId || started) return;
    if (holdLocked) {
      e.preventDefault();
      return;
    }
    const dist = Math.hypot(e.clientX - startX, e.clientY - startY);
    if (dist > TOUCH_MOVE_CANCEL_PX) clearHold();
  };

  const onUp = (e: PointerEvent) => {
    if (armedPointerId !== e.pointerId) return;
    clearHold(true);
  };

  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    if (opts?.isBlocked?.(e)) return;
    if (holdTimer != null || started) return;

    startX = e.clientX;
    startY = e.clientY;
    armedPointerId = e.pointerId;

    const begin = () => {
      if (armedPointerId !== e.pointerId || started) return;
      started = true;
      if (holdTimer != null) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);

      // Lock scroll as soon as the hold commits — before async ghost prep.
      if (!holdLocked) {
        holdLocked = true;
        lockInventoryDragScroll();
      }
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      e.preventDefault();
      onReady(e);
      // Consumer owns the scroll lock for the rest of the gesture via startInventoryCosmeticDrag.
      // Transfer ownership: do not unlock here; drag cleanup / prep-abort will unlock.
      holdLocked = false;
      armedPointerId = null;
      started = false;
    };

    if (isInventoryTouchPointer(e)) {
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      holdTimer = setTimeout(begin, INVENTORY_TOUCH_HOLD_MS);
    } else {
      begin();
    }
  };

  el.addEventListener("pointerdown", onDown);
  return () => {
    clearHold(true);
    el.removeEventListener("pointerdown", onDown);
  };
}

/**
 * Start a pointer-driven cosmetic drag. Resolves when the gesture ends.
 * Expects scroll to already be locked by bindInventoryDragPointer (hold commit).
 */
export function startInventoryCosmeticDrag(
  pointerId: number,
  startEvent: PointerEvent,
  opts: InventoryDragOpts,
): Promise<InventoryDragResult> {
  return new Promise((resolve) => {
    const { sourcePreview, sourceVisual, mode, ghostDisplaySize } = opts;

    const display = resolveGhostDisplaySize(sourceVisual, ghostDisplaySize);
    const ghostW = display.width;
    const ghostH = display.height;
    const startRect = sourceVisual.getBoundingClientRect();
    const returnX = startRect.width >= 8 ? startRect.left : startEvent.clientX - ghostW / 2;
    const returnY = startRect.height >= 8 ? startRect.top : startEvent.clientY - ghostH / 2;

    const layer = document.createElement("div");
    layer.className = "inventoryDragLayer";
    layer.setAttribute("aria-hidden", "true");

    const ghost = document.createElement("canvas");
    ghost.className = "inventoryDragGhost";
    ghost.width =
      sourceVisual instanceof HTMLCanvasElement && sourceVisual.width
        ? sourceVisual.width
        : Math.round(ghostW * 2);
    ghost.height =
      sourceVisual instanceof HTMLCanvasElement && sourceVisual.height
        ? sourceVisual.height
        : Math.round(ghostH * 2);
    ghost.style.width = `${ghostW}px`;
    ghost.style.height = `${ghostH}px`;
    copyVisualToCanvas(sourceVisual, ghost);
    layer.appendChild(ghost);
    document.body.appendChild(layer);

    sourcePreview.classList.add("inventoryCosmeticPreview--lifted");

    const hotZones = mode.kind === "equip" ? mode.dropZones : mode.keepZones;
    for (const z of hotZones) {
      if (mode.kind === "equip") {
        if (zoneAccepts(z, mode.category)) z.classList.add("inventoryDropZone--hot");
      } else {
        z.classList.add("inventoryDropZone--hot");
      }
    }

    let x = startEvent.clientX - ghostW / 2;
    let y = startEvent.clientY - ghostH / 2;
    let prevX = startEvent.clientX;
    let prevY = startEvent.clientY;
    let prevT = performance.now();
    let vx = 0;
    let vy = 0;
    let sx = 1;
    let sy = 1;
    let angle = 0;
    let activeZone: HTMLElement | null = null;
    let finished = false;

    const placeGhost = () => {
      const stretch = 1 + Math.min(0.5, Math.hypot(vx, vy) / 900);
      const compress = 1 / Math.sqrt(stretch);
      sx = stretch;
      sy = compress;
      angle = Math.atan2(vy, vx) || 0;
      ghost.style.transform = `translate(${x}px, ${y}px) rotate(${angle}rad) scale(${sx}, ${sy})`;
    };
    placeGhost();

    const highlightZone = (zone: HTMLElement | null) => {
      if (activeZone === zone) return;
      if (activeZone) activeZone.classList.remove("inventoryDropZone--over");
      activeZone = zone;
      if (activeZone) activeZone.classList.add("inventoryDropZone--over");
    };

    const cleanupUi = () => {
      for (const z of hotZones) {
        z.classList.remove("inventoryDropZone--hot", "inventoryDropZone--over");
      }
      sourcePreview.classList.remove("inventoryCosmeticPreview--lifted");
      unlockInventoryDragScroll();
    };

    const finish = async (clientX: number, clientY: number) => {
      if (finished) return;
      finished = true;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);

      highlightZone(null);

      if (mode.kind === "equip") {
        const zone = hitDropZone(clientX, clientY, mode.dropZones, mode.category);
        if (zone) {
          layer.style.visibility = "hidden";
          let ok = false;
          try {
            ok = await mode.onDropEquip();
          } catch {
            ok = false;
          }
          layer.style.visibility = "visible";
          if (ok) {
            const zr = zone.getBoundingClientRect();
            const targetX = zr.left + zr.width / 2 - ghostW / 2;
            const targetY = zr.top + zr.height / 2 - ghostH / 2;
            await animateTransform(
              ghost,
              { x, y, sx: 1, sy: 1 },
              { x: targetX, y: targetY, sx: 1, sy: 1 },
              SETTLE_MS,
              true,
            );
            cleanupUi();
            layer.remove();
            resolve("equipped");
            return;
          }
        }
      } else {
        const kept = hitZoneList(clientX, clientY, mode.keepZones);
        if (!kept) {
          layer.style.visibility = "hidden";
          let ok = false;
          try {
            ok = await mode.onUnequip();
          } catch {
            ok = false;
          }
          layer.style.visibility = "visible";
          if (ok) {
            await animateTransform(
              ghost,
              { x, y, sx, sy },
              { x: x + 8, y: y + 24, sx: 0.2, sy: 0.2 },
              SETTLE_MS * 0.7,
              false,
            );
            cleanupUi();
            layer.remove();
            resolve("unequipped");
            return;
          }
        }
      }

      await animateTransform(
        ghost,
        { x, y, sx, sy },
        { x: returnX, y: returnY, sx: 1, sy: 1 },
        RETURN_MS,
        true,
      );
      cleanupUi();
      layer.remove();
      resolve("cancelled");
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      e.preventDefault();
      const now = performance.now();
      const dt = Math.max(1, now - prevT);
      const cx = e.clientX;
      const cy = e.clientY;
      const rawVx = ((cx - prevX) / dt) * 1000;
      const rawVy = ((cy - prevY) / dt) * 1000;
      vx = vx * 0.65 + rawVx * 0.35;
      vy = vy * 0.65 + rawVy * 0.35;
      prevX = cx;
      prevY = cy;
      prevT = now;
      x = cx - ghostW / 2;
      y = cy - ghostH / 2;
      placeGhost();
      if (mode.kind === "equip") {
        highlightZone(hitDropZone(cx, cy, mode.dropZones, mode.category));
      } else {
        highlightZone(hitZoneList(cx, cy, mode.keepZones));
      }
    };

    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      void finish(e.clientX, e.clientY);
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  });
}
